import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardPen, Landmark, FileText, UtensilsCrossed, Users, GitCompareArrows, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard-nav-item" },
  { to: "/entry", label: "Daily Entry", icon: ClipboardPen, id: "daily-entry-nav-item" },
  { to: "/fixed-costs", label: "Fixed Costs", icon: Landmark, id: "fixed-costs-nav-item" },
  { to: "/staff", label: "Staff Salary", icon: Users, id: "staff-salary-nav-item" },
  { to: "/compare", label: "Compare", icon: GitCompareArrows, id: "compare-nav-item" },
  { to: "/reports", label: "Reports & PDF", icon: FileText, id: "reports-nav-item" },
  { to: "/menu", label: "Menu & Settings", icon: UtensilsCrossed, id: "menu-settings-nav-item" },
];

const Brand = () => (
  <div className="flex items-center gap-3 px-2">
    <div className="h-10 w-10 rounded-xl bg-amber-600 text-white grid place-items-center font-extrabold text-lg shadow-sm">M</div>
    <div className="leading-tight">
      <div className="font-extrabold tracking-tight text-slate-900">MANJAP</div>
      <div className="text-[11px] text-slate-500">Good Food. Great Mood.</div>
    </div>
  </div>
);

const NavItems = ({ onClick }) => (
  <nav className="mt-8 space-y-1">
    {NAV.map(({ to, label, icon: Icon, id }) => (
      <NavLink key={to} to={to} end={to === "/"} onClick={onClick} data-testid={id}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
            isActive ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
        <Icon className="h-4 w-4" />{label}
      </NavLink>
    ))}
  </nav>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const out = () => { logout(); nav("/login"); };

  const Footer = () => (
    <div className="mt-auto border-t border-slate-200 pt-4 px-2">
      <div className="text-xs text-slate-500 truncate" data-testid="current-user-email">{user?.email}</div>
      <button onClick={out} data-testid="logout-button"
        className="mt-2 flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600 transition-colors">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#FBFAF7]">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-5 sticky top-0 h-screen">
        <Brand /><NavItems /><Footer />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white p-5 flex flex-col shadow-xl">
            <button className="self-end mb-2" onClick={() => setOpen(false)} data-testid="mobile-nav-close"><X className="h-5 w-5" /></button>
            <Brand /><NavItems onClick={() => setOpen(false)} /><Footer />
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3">
          <Brand />
          <button onClick={() => setOpen(true)} data-testid="mobile-nav-open"><Menu className="h-5 w-5" /></button>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">{children}</main>
      </div>
    </div>
  );
}
